/**
 * Symbol grammar for the Ordinals Wallet datafeed.
 *
 * TradingView addresses everything by a symbol string, so every chart on the
 * site — collections, runes, alkanes, BTC — has to be nameable in one. The
 * grammar is `OW:<kind>:<id>:<denom>`, e.g. `OW:collection:bitcoin-frogs:usd`.
 *
 * Denomination is part of the symbol rather than a chart setting because
 * TradingView caches bars per symbol. If sats and USD shared a symbol, flipping
 * the denomination would redraw the old series' bars under the new scale until
 * the cache turned over, which reads as the chart showing the wrong price.
 *
 * **The id may itself contain colons.** An alkane is addressed `block:tx`, so
 * `OW:alkane:2:0:usd` has five colon-separated pieces, not four. Parsing must
 * anchor on the ends — prefix and kind at the front, denomination at the back —
 * and treat everything between as the id. Splitting naively and requiring four
 * parts rejects every alkane symbol as unknown, which is exactly how the
 * alkanes chart came up empty with "Symbol Error: unknown symbol".
 */

export type ChartKind = 'collection' | 'market' | 'alkane'

/** What the numbers on the price axis mean. */
export type Denom = 'sats' | 'usd' | 'mcap'

export interface ParsedSymbol {
	kind: ChartKind
	id: string
	denom: Denom
}

const DENOMS: Denom[] = ['sats', 'usd', 'mcap']

export function buildSymbol(kind: ChartKind, id: string, denom: Denom): string {
	return `OW:${kind}:${id}:${denom}`
}

/**
 * Parse a symbol back into its parts.
 *
 * Returns `null` rather than throwing or guessing: TradingView asks the
 * datafeed to resolve arbitrary strings (saved chart state, a symbol search
 * the user typed), and a malformed one must produce an honest "unknown
 * symbol" error rather than a chart of the wrong thing.
 */
export function parseSymbol(symbol: string): ParsedSymbol | null {
	const parts = symbol.split(':')
	// prefix + kind + at least one id segment + denom
	if (parts.length < 4 || parts[0] !== 'OW') {
		return null
	}

	const kind = parts[1]
	if (kind !== 'collection' && kind !== 'market' && kind !== 'alkane') {
		return null
	}

	const denom = parts[parts.length - 1]
	if (!DENOMS.includes(denom as Denom)) {
		return null
	}

	// Everything between kind and denomination is the id, colons and all.
	const id = parts.slice(2, -1).join(':')
	if (!id) {
		return null
	}

	return { kind, id, denom: denom as Denom }
}

/** Swap the denomination of a symbol, keeping the asset. */
export function withDenom(symbol: string, denom: Denom): string {
	const parsed = parseSymbol(symbol)
	return parsed ? buildSymbol(parsed.kind, parsed.id, denom) : symbol
}

/**
 * TradingView resolution strings mapped to the API's interval vocabulary.
 * Resolutions are minutes as a bare number, or a `D`/`W` suffix.
 */
export function resolutionToInterval(resolution: string): string | null {
	switch (resolution) {
		case '5':
			return '5m'
		case '15':
			return '15m'
		case '60':
			return '1h'
		case '240':
			return '4h'
		case '720':
			return '12h'
		case '1D':
		case 'D':
			return '1d'
		case '1W':
		case 'W':
			return '1w'
		default:
			return null
	}
}

/** Bucket width in seconds for a resolution, for windowing requests. */
export function resolutionSeconds(resolution: string): number {
	switch (resolution) {
		case '5':
			return 300
		case '15':
			return 900
		case '60':
			return 3600
		case '240':
			return 14400
		case '720':
			return 43200
		case '1D':
		case 'D':
			return 86400
		case '1W':
		case 'W':
			return 604800
		default:
			return 86400
	}
}

/**
 * What the toolbar offers. Four hours is the finest: below it a collection's
 * bars are mostly empty buckets, and the mark is a smoother line than a
 * finer chart can honestly draw. The finer intervals stay in the API for
 * tooling; the chart does not show them.
 */
export const SUPPORTED_RESOLUTIONS = ['240', '720', '1D', '1W']

/** The resolution a chart opens at: a day per candle, the bar most
 * screenshots and most other venues are read in. */
export const DEFAULT_RESOLUTION = '1D'

/**
 * Price formatting hint for TradingView's `pricescale`.
 *
 * Satoshi prices are integers spanning many orders of magnitude (a cheap
 * inscription trades in thousands of sats, a grail in billions), so they get
 * no fractional digits. Dollar prices need cents. Marketcaps are large enough
 * that cents are noise.
 */
/**
 * The `pricescale` for a series whose typical price is known: enough
 * fractional digits that the axis can place ticks between prices.
 *
 * `pricescale` is the library's notion of the smallest price step, and its
 * tick generator will not put two labels closer than that step. With the
 * fixed per-denomination scale a TAP token at 0.00007953 sats had a step of
 * one whole sat -- every tick rounded to the same place and the axis drew
 * nothing at all. Four significant digits at the typical price gives a
 * useful step at every magnitude: 1 at a 30M market cap, 1e-8 at that
 * token, 0.01 at a $4 coin.
 */
export function priceScaleForPrice(typical: number): number {
	if (!Number.isFinite(typical) || typical <= 0) {
		return 1
	}
	const magnitude = Math.floor(Math.log10(typical))
	const decimals = Math.max(0, 3 - magnitude)
	// The library takes an integer; a step finer than 1e-12 is past what a
	// double can carry through its own arithmetic anyway.
	return 10 ** Math.min(decimals, 12)
}

export function priceScaleFor(denom: Denom): number {
	switch (denom) {
		case 'usd':
			return 100
		case 'mcap':
			return 1
		case 'sats':
		default:
			return 1
	}
}
